import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Car, Plus, Wrench, AlertTriangle, CheckCircle, Clock,
  ChevronRight, Gauge, Calendar
} from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';
import GlobalHeader from '@/components/GlobalHeader';
import AddVehicleDialog from '@/components/car-maintenance/AddVehicleDialog';
import VehicleDetailView from '@/components/car-maintenance/VehicleDetailView';
import APP_ICONS, { PastelIcon } from '@/utils/appIcons';
import { useDemo } from '@/contexts/DemoContext';
import { ProtectedFeaturePage } from '@/components/ProtectedFeaturePage';

/**
 * CarMaintenancePage - Vehicle records and maintenance tracking
 * PREMIUM FEATURE - Requires Premium subscription
 */

const STATUS_COLORS = {
  good: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  due_soon: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  action_needed: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
  unknown: { bg: 'bg-slate-100', text: 'text-slate-500', icon: Car },
};

const STATUS_LABELS = {
  good: 'Looking Good',
  due_soon: 'Due Soon',
  action_needed: 'Needs Attention',
  unknown: 'Ready to Set Up',
};

const CarMaintenanceContent = () => {
  const { isDemoMode, getVehicles } = useDemo();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleStatuses, setVehicleStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchVehicles();
    fetchAllStatuses();
    
    // Check for vehicle ID in URL
    const vehicleId = searchParams.get('vehicle');
    if (vehicleId) {
      setSelectedVehicleId(vehicleId);
    }
  }, [isDemoMode]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const demoVehicles = getVehicles() || [];
        setVehicles(demoVehicles);
        setLoading(false);
        return;
      }
      
      const response = await API.get('/vehicles');
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      // Only show error toast for non-demo mode failures
      if (!isDemoMode) {
        toast.error('Failed to load vehicles');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStatuses = async () => {
    if (isDemoMode) {
      // Generate demo statuses from demo vehicles
      const demoVehicles = getVehicles() || [];
      const statusMap = {};
      demoVehicles.forEach(v => {
        statusMap[v.id] = {
          id: v.id,
          status: v.maintenance_status || 'good',
          next_service: v.next_service_date
        };
      });
      setVehicleStatuses(statusMap);
      return;
    }
    
    try {
      const response = await API.get('/vehicles/home-status');
      const statusMap = {};
      (response.data?.vehicles || []).forEach(v => {
        statusMap[v.id] = v;
      });
      setVehicleStatuses(statusMap);
    } catch (error) {
      console.error('Failed to fetch vehicle statuses:', error);
    }
  };

  const handleRefresh = async () => {
    await Promise.all([fetchVehicles(), fetchAllStatuses()]);
    toast.success('Data refreshed');
  };

  const handleAddVehicle = async (vehicleData) => {
    try {
      const response = await API.post('/vehicles', vehicleData);
      toast.success('Vehicle added successfully!', {
        description: "You're all set to start tracking maintenance.",
        duration: 4000,
      });
      fetchVehicles();
      fetchAllStatuses();
      setShowAddVehicle(false);
      // Navigate to the new vehicle
      setSelectedVehicleId(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vehicle');
    }
  };

  const handleSelectVehicle = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    setSearchParams({ vehicle: vehicleId });
  };

  const handleBackToList = () => {
    setSelectedVehicleId(null);
    setSearchParams({});
    fetchAllStatuses();
  };

  // If a vehicle is selected, show detail view
  if (selectedVehicleId) {
    const vehicle = vehicles.find(v => v.id === selectedVehicleId);
    if (vehicle) {
      return (
        <VehicleDetailView
          vehicle={vehicle}
          onBack={handleBackToList}
          onRefresh={handleRefresh}
        />
      );
    }
  }

  // Empty state
  if (!loading && vehicles.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50" style={{ backgroundColor: '#EAF7F6' }}>
        <GlobalHeader
          iconSrc={APP_ICONS.carMaintenance}
          iconBgColor="#BFE3E0"
          iconColor="text-teal-700"
          title="Vehicles"
          subtitle="Your car health dashboard"
          onRefresh={handleRefresh}
        />

        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="text-center max-w-md px-4">
            <div className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#BFE3E0' }}>
              <PastelIcon src={APP_ICONS.carMaintenance} alt="Car" size={48} />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Add Your First Vehicle</h2>
            <p className="text-gray-600 mb-8">
              Start tracking maintenance, receipts, and protection to keep your vehicles running smoothly.
            </p>
            <Button
              onClick={() => setShowAddVehicle(true)}
              size="lg"
              style={{ backgroundColor: '#8ECFC9' }}
              className="hover:opacity-90"
              data-testid="add-vehicle-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Vehicle
            </Button>
          </div>
        </div>

        <AddVehicleDialog
          open={showAddVehicle}
          onOpenChange={setShowAddVehicle}
          onSubmit={handleAddVehicle}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #EAF7F6 0%, #FFFFFF 50%, #D5EFEC 100%)' }}>
      <GlobalHeader
        iconSrc={APP_ICONS.carMaintenance}
        iconBgColor="#BFE3E0"
        iconColor="text-teal-700"
        title="Vehicles"
        subtitle="Your car health dashboard"
        onRefresh={handleRefresh}
      />

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-28">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            onClick={() => setShowAddVehicle(true)}
            style={{ backgroundColor: '#8ECFC9' }}
            className="hover:opacity-90"
            data-testid="add-vehicle-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle
          </Button>
        </div>

        {/* Vehicle Cards Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-48 rounded-xl animate-pulse" style={{ backgroundColor: '#D5EFEC' }} />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map((vehicle) => {
              const status = vehicleStatuses[vehicle.id] || {};
              const statusInfo = STATUS_COLORS[status.status] || STATUS_COLORS.unknown;
              const StatusIcon = statusInfo.icon;
              const vehicleName = `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() || vehicle.nickname || 'Unnamed Vehicle';
              
              return (
                <Card
                  key={vehicle.id}
                  className="cursor-pointer transition-all duration-200 hover:shadow-lg active:scale-[0.98] overflow-hidden border-0"
                  onClick={() => handleSelectVehicle(vehicle.id)}
                  style={{ backgroundColor: '#F5FAFA', borderLeft: `4px solid ${status.status === 'action_needed' ? '#EF4444' : status.status === 'due_soon' ? '#F59E0B' : '#22C55E'}` }}
                  data-testid={`vehicle-card-${vehicle.id}`}
                >
                  <CardContent className="p-5">
                    {/* Vehicle Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#BFE3E0' }}>
                          <Car className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{vehicleName}</h3>
                          {vehicle.nickname && vehicle.make && (
                            <p className="text-xs text-slate-500">{vehicle.nickname}</p>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>

                    {/* Mileage */}
                    {vehicle.current_mileage && (
                      <div className="flex items-center gap-2 mb-3 text-sm text-slate-600">
                        <Gauge className="h-4 w-4" />
                        <span>{vehicle.current_mileage.toLocaleString()} miles</span>
                      </div>
                    )}

                    {/* Status Badge */}
                    <div className="flex items-center justify-between">
                      <Badge className={`${statusInfo.bg} ${statusInfo.text} gap-1`}>
                        <StatusIcon className="h-3 w-3" />
                        {STATUS_LABELS[status.status] || 'Unknown'}
                      </Badge>
                      
                      {status.most_urgent && (
                        <span className="text-xs text-slate-500 truncate max-w-[150px]">
                          {status.most_urgent.name}
                        </span>
                      )}
                    </div>

                    {/* Urgent/Upcoming Counts */}
                    {(status.urgent_count > 0 || status.upcoming_count > 0) && (
                      <div className="flex gap-3 mt-3 pt-3 border-t border-slate-100">
                        {status.urgent_count > 0 && (
                          <span className="text-xs text-red-600 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {status.urgent_count} overdue
                          </span>
                        )}
                        {status.upcoming_count > 0 && (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {status.upcoming_count} due soon
                          </span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <AddVehicleDialog
        open={showAddVehicle}
        onOpenChange={setShowAddVehicle}
        onSubmit={handleAddVehicle}
      />
    </div>
  );
};

// Wrap content with feature protection - Premium tier required
const CarMaintenancePage = () => (
  <ProtectedFeaturePage
    feature="vehicle_records"
    title="Vehicle Records"
    description="Track your vehicles, maintenance schedules, and service history. Never miss an oil change or inspection again."
    backPath="/"
  >
    <CarMaintenanceContent />
  </ProtectedFeaturePage>
);

export default CarMaintenancePage;
